/**
 * Flow Optimizer — queue query hook
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { getQueue } from '../services/flow-optimizer-queue-api';
import { type FlowOptimizerProvider } from '../services/flow-optimizer-api';

/** Shared query-key factory so mutations can invalidate the queue. Keys take
 *  any tracker (cache-only callers); only Voluum/Binom queries ever exist. */
export const queueKeys = {
  queue: (provider: FlowOptimizerProvider, campaignId: string) =>
    [provider, 'flow-optimizer', 'queue', campaignId] as const,
  simulatedStats: (provider: FlowOptimizerProvider, campaignId: string) =>
    [provider, 'flow-optimizer', 'simulated-stats', campaignId] as const,
  rotationHistory: (provider: FlowOptimizerProvider, campaignId: string) =>
    [provider, 'flow-optimizer', 'rotation-history', campaignId] as const,
  queueEvents: (
    provider: FlowOptimizerProvider,
    campaignId: string,
    layer: string,
    bankId: string,
  ) =>
    [provider, 'flow-optimizer', 'queue-events', campaignId, layer, bankId] as const,
};

export function useFlowOptimizerQueue(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  return useQuery({
    queryKey: queueKeys.queue(provider, campaignId),
    queryFn: () => getQueue(campaignId, provider),
    enabled: Boolean(campaignId),
    staleTime: 15_000,
  });
}
